import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Menu, MenuItem } from '@mui/material';
import type { MessageGenPhase } from '@trinity/chat';
import { PhaseBadge } from './badges/PhaseBadge';
import { TypeBadge } from './badges/TypeBadge';
import { SessionHistoryDropdown } from './SessionHistoryDropdown';
import {
  MessageGenerationTableActionsValueContext,
  MessageGenerationStateContext,
} from '../../screens/MessageGenSession/Context';

export interface MessageGenerationSessionHeaderProps {
  sessionId?: number;
  sessionName: string;
  createdOn: Date | string;
  phase: MessageGenPhase;
  type: 'run' | 'segment';
  brandName?: string;
  objective?: string | null;
  showPageTitle?: boolean;
  showSessionHistory?: boolean;
  sessionHistoryIconOnly?: boolean;
  onSelectSession?: (sessionId: number) => void;
}

/**
 * Check if messages can be saved based on the current phase.
 */
function canSaveMessages(phase?: MessageGenPhase): boolean {
  return phase === 'curated';
}

export function MessageGenerationSessionHeader({
  sessionId,
  sessionName,
  createdOn,
  phase,
  type,
  brandName,
  objective,
  showPageTitle = true,
  showSessionHistory = true,
  sessionHistoryIconOnly = false,
  onSelectSession,
}: MessageGenerationSessionHeaderProps): React.JSX.Element {
  const tableActions = useContext(MessageGenerationTableActionsValueContext);
  const session = useContext(MessageGenerationStateContext);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);

  const hasSessionName = sessionName && sessionName.trim().length > 0;
  const showDivider = showPageTitle && hasSessionName;

  const formatDateTime = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}/${day} ${displayHours}:${minutes} ${ampm}`;
  };

  const savingEnabled = canSaveMessages(tableActions?.phase);

  return (
    <HeaderRoot>
      <HeaderLeft>
        {showPageTitle && (
          <>
            <PageTitle>Message Generation</PageTitle>
            {showDivider && <Divider>|</Divider>}
          </>
        )}
        {hasSessionName && <SessionName>{sessionName}</SessionName>}
      </HeaderLeft>

      <HeaderRight>
        <PhaseBadge phase={phase} />
        {brandName && (
          <BrandItem>
            {brandName}
          </BrandItem>
        )}
        {objective && (
          <ObjectiveItem>
            {objective}
          </ObjectiveItem>
        )}
        {showSessionHistory && onSelectSession && (
          <SessionHistoryDropdown
            currentSessionId={sessionId}
            onSelectSession={onSelectSession}
            iconOnly={sessionHistoryIconOnly}
          />
        )}
        <ActionsButton onClick={(e) => setActionsAnchorEl(e.currentTarget)}>
          Actions
        </ActionsButton>
        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={() => setActionsAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              if (!tableActions) return;
              const unsavedSelected = (tableActions.selectionModel as string[]).filter((id) => {
                const message = tableActions.messages.find((m) => m.identifier === id);
                return message && !message.saved;
              });
              if (unsavedSelected.length > 0) {
                tableActions.onSaveSelected(unsavedSelected);
              }
              setActionsAnchorEl(null);
            }}
            disabled={!tableActions || (() => {
              if (!savingEnabled) return true;
              const unsavedCount = (tableActions.selectionModel as string[]).filter((id) => {
                const message = tableActions.messages.find((m) => m.identifier === id);
                return message && !message.saved;
              }).length;
              return unsavedCount === 0 || !tableActions.sessionId;
            })()}
          >
            Save Selected Messages (
            {tableActions ? (() => {
              const unsavedCount = (tableActions.selectionModel as string[]).filter((id) => {
                const message = tableActions.messages.find((m) => m.identifier === id);
                return message && !message.saved;
              }).length;
              return unsavedCount;
            })() : 0}
            )
          </MenuItem>
          <MenuItem
            onClick={() => {
              const params = new URLSearchParams();
              if (session?.sessionId) params.set('segmentationSessionId', session.sessionId);
              if (session?.id) params.set('messageGenerationSessionId', String(session.id));
              if (session?.state.segmentIds?.length === 1) params.set('segmentId', session.state.segmentIds[0]);
              const qs = params.toString();
              window.open(`/message-library${qs ? `?${qs}` : ''}`, '_blank');
              setActionsAnchorEl(null);
            }}
          >
            View Message Library
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!tableActions) return;
              tableActions.onResetSession();
              setActionsAnchorEl(null);
            }}
            disabled={!tableActions}
          >
            Reset Session
          </MenuItem>
        </Menu>
      </HeaderRight>
    </HeaderRoot>
  );
}

const HeaderRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  margin-bottom: 12px;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #626262;
  white-space: nowrap;
`;

const Divider = styled.div`
  font-size: 16px;
  color: #e5e5e5;
  font-weight: 300;
`;

const SessionName = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #323232;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderRight = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
`;

const BrandItem = styled(MetadataItem)`
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
`;

const ObjectiveItem = styled(MetadataItem)`
  background: #e9d5ff;
  border: 1px solid #d8b4fe;
  color: #6b21a8;
`;

const DateTimeBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  background: #e0e7ff;
  color: #4338ca;
`;

const ActionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${(props) => props.theme.palette.sentiment.primary.main};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${(props) => props.theme.palette.sentiment.primary.dark};
  }
`;
